import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import { 
  ExpandMore,
  Settings,
  Timer,
} from "@material-ui/icons";
import api from "../../services/api";

const deepseekModels = [
  "deepseek-chat",
  "deepseek-coder"
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  temporarySettings: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.light}`
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    }
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column"
  }
}));

const DeepSeekSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(deepseekModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(8000, "Máximo 8000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
});


const FlowBuilderDeepSeekModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "deepseek-chat",
    maxTokens: 1000,
    temperature: 0.7,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false,
    kanbanClassificationMode: "none",
    kanbanLaneId: null,
    kanbanUserId: null,
    tagClassificationMode: "none",
    tagIds: [],
    queueClassificationMode: "none",
    useContextDatabase: false,
    contextDatabasePrompt: ""
  };

  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar DeepSeek ao fluxo",
    btn: "Adicionar",
  });
  const [lanes, setLanes] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [queues, setQueues] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar DeepSeek do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      
      let kanbanClassificationMode = "none";
      if (typebotIntegration.autoClassifyToKanban) {
        kanbanClassificationMode = "auto";
      } else if (typebotIntegration.kanbanLaneId) {
        kanbanClassificationMode = "manual";
      }

      let tagClassificationMode = "none";
      if (typebotIntegration.autoClassifyToTag) {
        tagClassificationMode = "auto";
      } else if (typebotIntegration.tagIds && typebotIntegration.tagIds.length > 0) {
        tagClassificationMode = "manual";
      }

      let queueClassificationMode = "none";
      if (typebotIntegration.autoClassifyToQueue) {
        queueClassificationMode = "auto";
      } else if (typebotIntegration.queueId) {
        queueClassificationMode = "manual";
      }
      
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        kanbanClassificationMode,
        tagClassificationMode,
        queueClassificationMode,
        model: deepseekModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "deepseek-chat",
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar DeepSeek ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
    }

    if (open) {
      loadLanesAndUsers();
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const loadLanesAndUsers = async () => {
    try {
      const [lanesRes, usersRes, tagsRes, queuesRes] = await Promise.all([
        api.get("/tag/kanban"),
        api.get("/users/"),
        api.get("/tags"),
        api.get("/queue")
      ]);
      
      setLanes(lanesRes.data.lista || []);
      setUsers(usersRes.data.users || []);
      
      const tagsData = tagsRes.data.tags || [];
      setTags(Array.isArray(tagsData) ? tagsData : []);
      
      const queuesData = Array.isArray(queuesRes.data) ? queuesRes.data : (queuesRes.data.queues || []);
      setQueues(queuesData);
    } catch (error) {
      console.log("Erro ao carregar dados:", error);
    }
  };

  const handleClose = () => {
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const promptData = {
      ...values,
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      provider: "deepseek",
      autoClassifyToKanban: values.kanbanClassificationMode === "auto",
      kanbanLaneId: values.kanbanClassificationMode === "manual" ? values.kanbanLaneId : null,
      kanbanUserId: values.kanbanClassificationMode === "manual" ? values.kanbanUserId : null,
      autoClassifyToTag: values.tagClassificationMode === "auto",
      tagIds: values.tagClassificationMode === "manual" ? values.tagIds : [],
      autoClassifyToQueue: values.queueClassificationMode === "auto",
      useContextDatabase: values.useContextDatabase,
      contextDatabasePrompt: values.useContextDatabase ? values.contextDatabasePrompt : ""
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const getModelDisplayName = (model) => {
    const modelNames = {
      "deepseek-chat": "💬 DeepSeek Chat",
      "deepseek-coder": "💻 DeepSeek Coder"
    };
    return modelNames[model] || model;
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {labels.title}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={DeepSeekSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configurações Básicas - DeepSeek
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <Field
                      as={TextField}
                      label="Nome do Assistente"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <Field
                      as={TextField}
                      label="API Key DeepSeek"
                      name="apiKey"
                      error={touched.apiKey && Boolean(errors.apiKey)}
                      helperText="Insira sua chave de API do DeepSeek"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      type="password"
                      placeholder="sk-..."
                    />

                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel>Modelo DeepSeek</InputLabel>
                      <Field
                        as={Select}
                        label="Modelo DeepSeek"
                        name="model"
                      >
                        {deepseekModels.map((model) => (
                          <MenuItem key={model} value={model}>
                            {getModelDisplayName(model)}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>

                    <Field
                      as={TextField}
                      label="Prompt do Sistema"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={6}
                      multiline
                      placeholder="Descreva como a IA deve se comportar..."
                    />

                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px", marginBottom: "16px" }}>
                      📚 Dica: Adicione instruções detalhadas para melhorar a qualidade das respostas da IA
                    </div>

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText="0 = conservador, 2 = criativo"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{ step: "0.1", min: "0", max: "2" }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText="Tamanho máximo da resposta"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText="Histórico de contexto"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                    </div>

                  </AccordionDetails>
                </Accordion>

                {/* COMPORTAMENTO DO FLUXO */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Timer />
                      Comportamento do Fluxo
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label="Permanente - Encerrar fluxo aqui"
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label="Temporário - Volta ao fluxo depois"
                        />
                      </RadioGroup>
                    </FormControl>

                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          ⏱️ Configurações do Modo Temporário
                        </Typography>

                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                            />
                          }
                          label="Auto completar quando atingir objetivo"
                        />
                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* CLASSIFICAÇÃO NO KANBAN */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Classificação no Kanban
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal" fullWidth>
                      <FormLabel component="legend">Modo de Classificação</FormLabel>
                      <RadioGroup
                        name="kanbanClassificationMode"
                        value={values.kanbanClassificationMode || "none"}
                        onChange={(e) => setFieldValue("kanbanClassificationMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label="Sem classificação automática"
                        />
                        <FormControlLabel
                          value="manual"
                          control={<Radio />}
                          label="Classificação manual - Selecionar coluna e usuário"
                        />
                        <FormControlLabel
                          value="auto"
                          control={<Radio />}
                          label="Classificação automática - IA escolhe coluna e usuário"
                        />
                      </RadioGroup>
                    </FormControl>

                    {values.kanbanClassificationMode === "manual" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          🎯 Selecione a Coluna e Usuário
                        </Typography>
                        
                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Coluna do Kanban</InputLabel>
                          <Field
                            as={Select}
                            label="Coluna do Kanban"
                            name="kanbanLaneId"
                            value={values.kanbanLaneId || ""}
                          >
                            <MenuItem value="">
                              <em>Selecione uma coluna</em>
                            </MenuItem>
                            {lanes.map((lane) => (
                              <MenuItem key={lane.id} value={lane.id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    width={12}
                                    height={12}
                                    borderRadius="50%"
                                    bgcolor={lane.color || "#757575"}
                                  />
                                  {lane.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>

                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Usuário para Atribuição</InputLabel>
                          <Field
                            as={Select}
                            label="Usuário para Atribuição"
                            name="kanbanUserId"
                            value={values.kanbanUserId || ""}
                          >
                            <MenuItem value="">
                              <em>Selecione um usuário</em>
                            </MenuItem>
                            {users.map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                {user.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Box>
                    )}

                    {values.kanbanClassificationMode === "auto" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          🤖 Classificação Automática pela IA
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          📋 Colunas Disponíveis
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                          {lanes.map((lane) => (
                            <Box
                              key={lane.id}
                              p={1}
                              bgcolor="white"
                              border="1px solid #e0e0e0"
                              borderRadius={1}
                              display="flex"
                              alignItems="center"
                              gap={1}
                            >
                              <Box
                                width={12}
                                height={12}
                                borderRadius="50%"
                                bgcolor={lane.color || "#757575"}
                              />
                              <Typography variant="caption">{lane.name}</Typography>
                            </Box>
                          ))}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          👥 Usuários Disponíveis
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                          {users.map((user) => (
                            <Box
                              key={user.id}
                              p={1}
                              bgcolor="white"
                              border="1px solid #e0e0e0"
                              borderRadius={1}
                            >
                              <Typography variant="caption">{user.name}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* CLASSIFICAÇÃO DE TAGS */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Classificação de Tags
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal" fullWidth>
                      <FormLabel component="legend">Modo de Classificação</FormLabel>
                      <RadioGroup
                        name="tagClassificationMode"
                        value={values.tagClassificationMode || "none"}
                        onChange={(e) => setFieldValue("tagClassificationMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label="Sem classificação de tags"
                        />
                        <FormControlLabel
                          value="manual"
                          control={<Radio />}
                          label="Classificação manual - Selecionar tags"
                        />
                        <FormControlLabel
                          value="auto"
                          control={<Radio />}
                          label="Classificação automática - IA escolhe tags"
                        />
                      </RadioGroup>
                    </FormControl>

                    {values.tagClassificationMode === "manual" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          🏷️ Selecione as Tags
                        </Typography>
                        
                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Tags</InputLabel>
                          <Field
                            as={Select}
                            label="Tags"
                            name="tagIds"
                            value={values.tagIds || []}
                            multiple
                          >
                            {tags.map((tag) => (
                              <MenuItem key={tag.id} value={tag.id}>
                                {tag.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Box>
                    )}

                    {values.tagClassificationMode === "auto" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          🤖 Classificação Automática pela IA
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          📋 Tags Disponíveis
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                          {tags.map((tag) => (
                            <Box
                              key={tag.id}
                              p={1}
                              bgcolor="white"
                              border="1px solid #e0e0e0"
                              borderRadius={1}
                            >
                              <Typography variant="caption">{tag.name}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* CLASSIFICAÇÃO DE FILAS */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Classificação de Filas
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal" fullWidth>
                      <FormLabel component="legend">Modo de Classificação</FormLabel>
                      <RadioGroup
                        name="queueClassificationMode"
                        value={values.queueClassificationMode || "none"}
                        onChange={(e) => setFieldValue("queueClassificationMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label="Sem classificação de filas"
                        />
                        <FormControlLabel
                          value="manual"
                          control={<Radio />}
                          label="Classificação manual - Selecionar fila"
                        />
                        <FormControlLabel
                          value="auto"
                          control={<Radio />}
                          label="Classificação automática - IA escolhe fila"
                        />
                      </RadioGroup>
                    </FormControl>

                    {values.queueClassificationMode === "manual" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          📞 Selecione a Fila
                        </Typography>
                        
                        <FormControl fullWidth margin="dense" variant="outlined">
                          <InputLabel>Fila</InputLabel>
                          <Field
                            as={Select}
                            label="Fila"
                            name="queueId"
                            value={values.queueId || ""}
                          >
                            <MenuItem value="">
                              <em>Selecione uma fila</em>
                            </MenuItem>
                            {queues.map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                {queue.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Box>
                    )}

                    {values.queueClassificationMode === "auto" && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Typography variant="subtitle2" gutterBottom>
                          🤖 Classificação Automática pela IA
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          📋 Filas Disponíveis
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                          {queues.map((queue) => (
                            <Box
                              key={queue.id}
                              p={1}
                              bgcolor="white"
                              border="1px solid #e0e0e0"
                              borderRadius={1}
                            >
                              <Typography variant="caption">{queue.name}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* BANCO DE DADOS DE CONTEXTO */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Banco de Dados de Contexto
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.useContextDatabase}
                          onChange={(e) => setFieldValue("useContextDatabase", e.target.checked)}
                        />
                      }
                      label="Usar banco de dados de contexto"
                    />

                    {values.useContextDatabase && (
                      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1} width="100%">
                        <Field
                          as={TextField}
                          label="Informações de Contexto"
                          name="contextDatabasePrompt"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={6}
                          placeholder="Adicione informações que a IA deve considerar..."
                        />
                      </Box>
                    )}

                  </AccordionDetails>
                </Accordion>

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderDeepSeekModal;
